import type { MessageGeneration, MessageSkillEvent, SkillStreamStatus } from '../types/chat';

const SKILL_EVENT_TYPES = new Set(['used', 'skipped', 'resource_loaded']);

/**
 * Payload inside ASSISTANT_STREAMED_RESPONSE.streamingResponse.data, e.g.
 * { skill_id, name, version } for SKILL_USED.
 */
function parseSkillEventBase(
  data: unknown
): Pick<MessageSkillEvent, 'skillId' | 'skillName' | 'version'> | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const o = data as Record<string, unknown>;
  const skillId = String(o.skill_id ?? o.skillId ?? o.id ?? '').trim();
  const skillName = String(o.name ?? o.skill_name ?? o.skillName ?? '').trim();
  if (!skillId && !skillName) {
    return null;
  }
  const version = parseOptionalString(o.version);
  return {
    skillId: skillId || skillName,
    skillName: skillName || skillId,
    ...(version && { version }),
  };
}

function parseOptionalString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

function parseDurationMs(data: Record<string, unknown>): number | undefined {
  const rawDuration = data.duration_ms ?? data.durationMs;
  if (typeof rawDuration === 'number' && Number.isFinite(rawDuration)) {
    return rawDuration;
  }
  if (typeof rawDuration === 'string') {
    const parsed = Number.parseFloat(rawDuration);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

export function streamEventTypeToSkillEventType(
  eventType: string
): MessageSkillEvent['type'] | null {
  switch (eventType.toUpperCase()) {
    case 'SKILL_USED':
      return 'used';
    case 'SKILL_SKIPPED':
      return 'skipped';
    case 'SKILL_RESOURCE_LOADED':
      return 'resource_loaded';
    default:
      return null;
  }
}

export function streamEventTypeToSkillStreamStatus(eventType: string): SkillStreamStatus | null {
  return streamEventTypeToSkillEventType(eventType);
}

/** WebSocket SKILL_* payload. */
export function parseSkillEventFromStreamPayload(
  eventType: string,
  data: unknown
): MessageSkillEvent | null {
  const type = streamEventTypeToSkillEventType(eventType);
  const base = parseSkillEventBase(data);
  if (!type || !base) {
    return null;
  }

  const o = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const resourceName = parseOptionalString(
    o.resource_name ?? o.resourceName ?? o.resource_title ?? o.resourceTitle
  );
  const reason = parseOptionalString(o.reason ?? o.skip_reason ?? o.skipReason);
  const error = o.error != null ? String(o.error) : o.Error != null ? String(o.Error) : null;

  return {
    ...base,
    type,
    ...(resourceName && { resourceName }),
    ...(reason && { reason }),
    ...(parseDurationMs(o) != null ? { durationMs: parseDurationMs(o) } : {}),
    error,
    status: type,
  };
}

/** Human-readable label from stream `name` (e.g. skill-creator → Skill Creator). */
const SKILL_NAME_ACRONYMS = new Set(['ui', 'a2ui', 'pdf', 'pptx', 'docx', 'xlsx', 'csv', 'kpi', 'api', 'sql']);

export function formatSkillDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) =>
      SKILL_NAME_ACRONYMS.has(part.toLowerCase())
        ? part.toUpperCase()
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join(' ');
}

function resolveSkillEventType(
  o: Record<string, unknown>,
  defaultTypeWhenMissing: boolean
): MessageSkillEvent['type'] | null {
  const rawType = String(o.type ?? o.event ?? '').toLowerCase();
  if (SKILL_EVENT_TYPES.has(rawType)) {
    return rawType as MessageSkillEvent['type'];
  }

  const fromStream = streamEventTypeToSkillEventType(String(o.type ?? o.event ?? ''));
  if (fromStream) {
    return fromStream;
  }

  const status = String(o.status ?? '').toLowerCase();
  if (SKILL_EVENT_TYPES.has(status)) {
    return status as MessageSkillEvent['type'];
  }

  return defaultTypeWhenMissing ? 'used' : null;
}

function parseSkillEventRecords(
  raw: unknown,
  options: { defaultTypeWhenMissing: boolean }
): MessageSkillEvent[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }

  const parsed: MessageSkillEvent[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const o = item as Record<string, unknown>;
    const base = parseSkillEventBase(o);
    if (!base) {
      continue;
    }

    const type = resolveSkillEventType(o, options.defaultTypeWhenMissing);
    if (!type) {
      continue;
    }

    const displayName = formatSkillDisplayName(base.skillName);

    parsed.push({
      ...base,
      skillName: displayName || base.skillName,
      type,
      resourceName: parseOptionalString(o.resource_name ?? o.resourceName),
      reason: parseOptionalString(o.reason ?? o.skip_reason ?? o.skipReason),
      error: o.error != null ? String(o.error) : null,
      durationMs: parseDurationMs(o),
      status: parseOptionalString(o.status) ?? type,
    });
  }

  return parsed.length > 0 ? parsed : undefined;
}

/**
 * Reads skill activity from message metadata after refresh.
 * - `skillEvents` / `skill_events`: full event log (requires event type)
 * - `skills`: persisted skill list from API (defaults each entry to "used")
 */
export function parseSkillEventsFromMetadata(metadata: unknown): MessageSkillEvent[] | undefined {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  const meta = metadata as Record<string, unknown>;
  const fromEventLog = parseSkillEventRecords(meta.skillEvents ?? meta.skill_events, {
    defaultTypeWhenMissing: false,
  });
  if (fromEventLog?.length) {
    return fromEventLog;
  }

  return parseSkillEventRecords(meta.skills, { defaultTypeWhenMissing: true });
}

export function upsertMessageSkillEvent(
  events: MessageSkillEvent[] | undefined,
  next: MessageSkillEvent
): MessageSkillEvent[] {
  return [...(events ?? []), next];
}

export function getSkillStreamStatusLabel(
  status: SkillStreamStatus,
  skillName?: string,
  resourceName?: string
): string {
  const name = skillName?.trim();
  const displayName = name ? formatSkillDisplayName(name) : '';
  switch (status) {
    case 'used':
      return displayName ? `Using ${displayName}` : 'Skill used';
    case 'skipped':
      return displayName ? `${displayName} skipped` : 'Skill skipped';
    case 'resource_loaded':
      if (resourceName) {
        return `Loaded ${resourceName}`;
      }
      return displayName ? `${displayName} resource loaded` : 'Skill resource loaded';
    default:
      return 'Skill activity';
  }
}

export function shouldRenderSkillStreamStatus(
  generation?: Pick<MessageGeneration, 'content' | 'skillStreamStatus'>
): boolean {
  if (!generation?.skillStreamStatus) {
    return false;
  }
  return !generation?.content?.trim();
}

export function clearSkillStreamStatusFields<T extends MessageGeneration>(generation: T): T {
  const { skillStreamStatus: _s, activeSkillName: _n, activeSkillResourceName: _r, ...rest } =
    generation;
  return rest as T;
}

export interface GroupedMessageSkillEvent {
  skillId: string;
  skillName: string;
  usedCount: number;
  skippedCount: number;
  resourceLoadedCount: number;
}

export function groupMessageSkillEventsBySkillId(
  events: MessageSkillEvent[]
): GroupedMessageSkillEvent[] {
  const grouped = new Map<string, GroupedMessageSkillEvent>();

  for (const event of events) {
    const key = event.skillId || event.skillName;
    const existing = grouped.get(key);
    if (existing) {
      if (event.type === 'used') existing.usedCount += 1;
      if (event.type === 'skipped') existing.skippedCount += 1;
      if (event.type === 'resource_loaded') existing.resourceLoadedCount += 1;
      continue;
    }
    grouped.set(key, {
      skillId: key,
      skillName: event.skillName || event.skillId,
      usedCount: event.type === 'used' ? 1 : 0,
      skippedCount: event.type === 'skipped' ? 1 : 0,
      resourceLoadedCount: event.type === 'resource_loaded' ? 1 : 0,
    });
  }

  return Array.from(grouped.values());
}
